import { useCallback, useEffect, useRef, useState } from 'react';

import { asApiError, type PSErrorClass } from '@/src/api/apiError';
import participationRequestsApi, {
  type ParticipationRequest,
} from '@/src/api/participationRequestsApi';

/**
 * useFetchParticipationRequests — `GET /me/participation-requests`, the "my
 * applications" list. No `id`, same shape as [[useFetchReservations]]: fetched
 * once on mount, and again on `retry` or `refresh`.
 */
export type FetchParticipationRequestsState = SettledState & {
  retry: () => void;
  refresh: () => Promise<void>;
};

type SettledState =
  | { status: 'loading'; requests: null; error: null }
  | { status: 'error'; requests: null; error: PSErrorClass }
  | { status: 'success'; requests: ParticipationRequest[]; error: null };

const LOADING: SettledState = {
  status: 'loading',
  requests: null,
  error: null,
};

export function useFetchParticipationRequests(): FetchParticipationRequestsState {
  const [state, setState] = useState<SettledState>(LOADING);
  const [attempt, setAttempt] = useState(0);
  const isMounted = useRef(true);

  const load = useCallback(async () => {
    try {
      const requests =
        await participationRequestsApi.listParticipationRequests();
      if (isMounted.current)
        setState({ status: 'success', requests, error: null });
    } catch (error: unknown) {
      if (isMounted.current)
        setState({ status: 'error', requests: null, error: asApiError(error) });
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();

    return () => {
      isMounted.current = false;
    };
  }, [attempt, load]);

  const retry = useCallback(() => {
    setState(LOADING);
    setAttempt((n) => n + 1);
  }, []);

  return { ...state, retry, refresh: load };
}
